import React, { useCallback, useEffect } from "react";
import { Product } from "tm-shared/types";

import { FilterDrawerUI } from "@/components/Filters/FilterDrawerUI";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useFilters } from "@/hooks/useFilters";
import { setRejectProductsSelectedFilters } from "@/store/slices/ui";

interface RejectFilterDrawerProps<TProduct extends Product> {
    open: boolean;
    setOpen: (value: boolean) => void;
    inventoryList: Record<string, number>;
    setFilteredProducts: (products: TProduct[]) => void;

    products: Record<string, TProduct>;
}

export function RejectFilterDrawer<TProduct extends Product>({
    open,
    setOpen,
    inventoryList,
    setFilteredProducts,
    products,
}: RejectFilterDrawerProps<TProduct>) {
    const dispatch = useAppDispatch();

    const { selectedFilters } = useAppSelector((s) => s.ui.rejectProducts);

    const { filteredProducts, availableFilters } = useFilters<TProduct>({
        products,
        inventoryList,
        filters: selectedFilters,
    });

    useEffect(() => {
        setFilteredProducts(filteredProducts);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters, setFilteredProducts]);

    const onClose = () => setOpen(false);

    const handleResetFilters = () => {
        dispatch(setRejectProductsSelectedFilters(null));
        onClose();
    };

    const handleFilterChange = useCallback(
        (tag: string, values: (string | number | boolean)[]) => {
            dispatch(setRejectProductsSelectedFilters({ [tag]: values }));
        },
        [dispatch],
    );

    return (
        <FilterDrawerUI
            onClose={onClose}
            onApply={onClose}
            onReset={handleResetFilters}
            isOpen={open}
            products={products}
            availableFilters={availableFilters}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            availableProductsLength={filteredProducts.length}
        />
    );
}
